import type { KyInstance } from "ky";
import { useSyncExternalStore } from "react";
import { parseEventStream } from "./eventStream";

/** One welcome-screen card, as carried by the agent's `capabilities` SSE event. */
export interface CapabilityCard {
  /** Unique card id — the sub-agent name, suffixed (`messaging:0`) when one agent exposes several cards. */
  id: string;
  /** Sub-agent name from the tiger_agent registry (e.g. `scheduling`). */
  agent?: string;
  label: string;
  description: string;
  /** Prompt to send when the card is clicked; null when the manifest offers none. */
  prompt?: string | null;
  examples?: string[];
}

// Mirrors tiger_agent's registry welcome cards. Shown only when no probe answers (see
// `capabilityCardsUnavailable`), so they can include a card the user's org has turned off.
export const DEFAULT_CAPABILITY_CARDS: CapabilityCard[] = [
  {
    id: "messaging:0",
    agent: "messaging",
    label: "Summarize conversation",
    description: "AI summary of a conversation",
    prompt: "Summarize this conversation",
  },
  {
    id: "scheduling",
    agent: "scheduling",
    label: "On-Call Lookup",
    description: "Find who's on call",
    prompt: "Who is on call right now?",
  },
  {
    id: "customer_support",
    agent: "customer_support",
    label: "Customer Support",
    description: "Get help with account, app, or provisioning issues, or share feedback",
    prompt: "One of my users isn't provisioning correctly",
  },
];

/** `data` payload of the `capabilities` event. */
interface CapabilitiesData {
  agent?: string;
  version?: string;
  cards?: CapabilityCard[];
}

/**
 * Bootstrap the welcome screen from the agent itself: a `{action: "capabilities"}`
 * invocation that the agent answers straight from its sub-agent registry — one
 * `capabilities` SSE event, then `done`. The server short-circuits before its graph,
 * spend gate, and session store, so this call mints no session and burns no budget.
 *
 * ky's default 10s timeout is kept deliberately: unlike a chat turn this stream is
 * tiny, and the caller falls back to static cards on failure, so hanging a cold
 * runtime start is worse than failing fast.
 */
export async function fetchCapabilities(
  api: KyInstance,
  opts: { signal?: AbortSignal } = {},
): Promise<CapabilityCard[]> {
  const response = await api.post("invocations", {
    signal: opts.signal,
    retry: 0,
    json: { action: "capabilities", stream: true },
  });

  for await (const { event, data } of parseEventStream<CapabilitiesData>(
    response,
    "Capabilities request",
  )) {
    if (event === "capabilities" && Array.isArray(data?.cards)) return data.cards;
  }

  throw new Error("Agent stream ended without a capabilities event");
}

/**
 * Longest the welcome screen shows loading cards after a probe is sent before falling back to the
 * defaults. The probe also boots the chat's microVM, so a cold start can outlast this; its cards
 * still replace the defaults whenever it answers.
 */
export const CARDS_MAX_WAIT_MS = 5_000;

/** What the welcome screen shows: loading cards, the agent's own, or the defaults. */
export interface WelcomeCards {
  cards: CapabilityCard[];
  /** True until a probe answers, fails, or `CARDS_MAX_WAIT_MS` passes; `cards` is empty meanwhile. */
  loading: boolean;
}

const LOADING: WelcomeCards = { cards: [], loading: true };
const FALLBACK: WelcomeCards = { cards: DEFAULT_CAPABILITY_CARDS, loading: false };

/**
 * The latest welcome cards an agent delivered for the current org, shared by every chat. The
 * pinned capabilities probe that warms each chat's session (`warmSession`) publishes them, so the
 * welcome screen needs no request of its own: a separate, unpinned one would boot a microVM no turn
 * ever reuses.
 *
 * An answered list is the agent's word, empty included: tiger_agent drops the cards of every
 * sub-agent the org hasn't enabled, so an empty list means this user gets none. The defaults stand
 * in only when no answer has come (the probe failed, or `CARDS_MAX_WAIT_MS` passed), and never
 * replace cards a probe already delivered. Cards differ per org, so an org switch starts over
 * (`resetCapabilityCards`).
 */
let current: WelcomeCards = LOADING;
const cardListeners = new Set<() => void>();
let waitTimer: ReturnType<typeof setTimeout> | undefined;
/**
 * Bumped by every reset. A probe records it when sent (`capabilityCardsRequested`) and hands it
 * back with its answer, so one sent before an org switch can't publish the old org's cards after.
 */
let generation = 0;

function clearWaitTimer(): void {
  clearTimeout(waitTimer);
  waitTimer = undefined;
}

function setCards(next: WelcomeCards): void {
  if (!next.loading) clearWaitTimer();
  current = next;
  for (const listener of cardListeners) listener();
}

/**
 * A probe is about to be sent: starts the `CARDS_MAX_WAIT_MS` wait while the cards are loading
 * (one wait however many probes are in flight). Returns the token its answer is published with.
 */
export function capabilityCardsRequested(): number {
  if (current.loading && waitTimer === undefined) {
    const requestedIn = generation;
    waitTimer = setTimeout(() => {
      waitTimer = undefined;
      capabilityCardsUnavailable(requestedIn);
    }, CARDS_MAX_WAIT_MS);
  }
  return generation;
}

/**
 * Record `cards` from a capabilities answer. Anything but a list counts as no answer. Ignored when
 * `requestedIn` predates the last reset (the probe was sent for another org).
 */
export function publishCapabilityCards(cards: unknown, requestedIn: number = generation): void {
  if (requestedIn !== generation) return;
  if (!Array.isArray(cards)) {
    capabilityCardsUnavailable(requestedIn);
    return;
  }
  setCards({ cards: cards as CapabilityCard[], loading: false });
}

/** No answer is coming (the probe failed or timed out): stop loading and show the defaults. */
export function capabilityCardsUnavailable(requestedIn: number = generation): void {
  if (requestedIn === generation && current.loading) setCards(FALLBACK);
}

/**
 * Forget the delivered cards and show loading again until the next probe answers. For an org
 * switch: the previous org's cards (or its empty list) say nothing about the new one's.
 */
export function resetCapabilityCards(): void {
  generation += 1;
  clearWaitTimer();
  setCards(LOADING);
}

function subscribeToCards(listener: () => void): () => void {
  cardListeners.add(listener);
  return () => cardListeners.delete(listener);
}

function cardsSnapshot(): WelcomeCards {
  return current;
}

/**
 * Welcome-screen cards: loading until a session's probe answers, then the agent's own (possibly
 * none). The defaults if the probe fails or takes longer than `CARDS_MAX_WAIT_MS`, so the welcome
 * screen never waits on the agent for long.
 */
export function useCapabilityCards(): WelcomeCards {
  return useSyncExternalStore(subscribeToCards, cardsSnapshot, cardsSnapshot);
}

/** Test hook: back to loading, as before any probe, without notifying anyone. */
export function resetCapabilityCardsForTest(): void {
  generation += 1;
  clearWaitTimer();
  current = LOADING;
}
